import type { UserRole } from '../types'
import { LIBERTY_ROLE_KEYS } from '../constants'

/**
 * `noemail` pipe equivalent — returns empty string for @noemail.com addresses.
 */
export function formatUserEmail(email: string | null | undefined): string {
  if (!email) return ''
  return email.includes('@noemail.com') ? '' : email
}

/**
 * `phone` pipe equivalent — mirrors Angular PhonePipe exactly.
 * Format: (XXX) XXX-XXXX  or  + X (XXX) XXX-XXXX  when country code is present.
 */
export function formatPhoneForDisplay(value: string | null | undefined): string {
  if (!value) return ''
  const cleaned = String(value).replace(/\D/g, '')
  const match = cleaned.match(/^([+]?\d{1,2}[.\-\s]?)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    const intlCode = match[1] ? '+ ' + match[1] : ''
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return value
}

/**
 * Phone input display formatter — mirrors Angular maskformatPhoneNumber exactly.
 * Only formats when the full regex matches (10+ digits). Returns raw input otherwise.
 * Angular: value="{{formatPhoneNumber(mobileNo.value)}}" calls maskformatPhoneNumber.
 */
export function formatPhoneInputDisplay(storedDigits: string): string {
  const raw = String(storedDigits ?? '')
  const cleaned = raw.replace(/\D/g, '')
  const match = cleaned.match(/^([+]?\d{1,2}[.\-\s]?)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    const intlCode = match[1] ? '+ ' + match[1] : ''
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return raw
}

/**
 * Extract raw digits from a phone string.
 * Angular allows up to 2-digit country code (\d{1,2}) + 10 phone digits = 12 max.
 */
export function cleanPhone(value: string): string {
  return String(value || '').replace(/\D/g, '').slice(0, 12)
}

/**
 * `titleName` pipe equivalent — capitalizes the first letter of each word.
 */
export function toTitleName(input: string): string {
  return input.replace(/\w\S*/g, (txt) => txt[0].toUpperCase() + txt.substr(1))
}

/**
 * Strip non-ASCII characters from username search (matching Angular behavior).
 */
export function stripNonAscii(input: string): string {
  return input.replace(/[\u0080-\uFFFF]/g, '')
}

const ROLE_DISPLAY_MAP: Record<string, string> = {
  ROLE_PROPERTY_MANAGER: 'Property Manager',
  ROLE_PROPERTY_AGENT: 'Leasing Consultant',
  ROLE_PROPERTY_ADMIN: 'Property Assistant',
  ROLE_COMPANY_MANAGER: 'Company Manager',
  ROLE_COMPANY_ADMIN: 'Company Admin',
  ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
  ROLE_RENTER: 'Resident',
  ROLE_APPLICANT: 'Applicant',
  ROLE_ATLANTA_OPEN_DOOR_ADMIN: 'Atlanta Open Door Admin',
  ROLE_ATLANTA_OPEN_DOOR_MANAGER: 'Atlanta Open Door Manager',
  
  // Liberty Internal Roles
  EXTERNAL_INSURANCE: 'External Insurance',
  ROLE_ADMIN: 'Liberty Admin',
  ROLE_CUSTOMER_SERVICE: 'Liberty Customer Service',
  ROLE_CUSTOMER_SERVICE_MANAGER: 'Liberty Customer Service Manager',
  ROLE_EXECS: 'Liberty Execs',
  ROLE_LIBERTY_ACCOUNTING: 'Liberty Accounting',
  ROLE_LIBERTY_DEFAULTS: 'Liberty Defaults',
  ROLE_LIBERTY_FIN_MGR: 'Liberty Finance Manager',
  ROLE_LIBERTY_SALES: 'Liberty Sales',
  ROLE_UNDERWRITER: 'Liberty Underwriter',
  ROLE_REFUND_APPROVAL: 'Liberty Refund Approval',
}

const formatRoleKey = (key: string): string => {
  return key
    .replace(/^ROLE_/, '')
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Build display name for a role row in the Edit User roles table.
 * Matches Angular template role display logic.
 */
export function buildRoleDisplayName(role: UserRole): string {
  const { role_name, company_name, property_name, org_name } = role
  const finMgr = role.fin_mgr_attribute === 'YES' ? ' (Finance Manager)' : ''

  const displayName = ROLE_DISPLAY_MAP[role_name] || formatRoleKey(role_name)

  if (role_name === 'ROLE_REGIONAL_MANAGER' && company_name && property_name) {
    return `${company_name} (${property_name}) - ${displayName}${finMgr}`
  }
  if (
    (role_name === 'ROLE_COMPANY_MANAGER' || role_name === 'ROLE_COMPANY_ADMIN') &&
    company_name
  ) {
    return `${company_name} - ${displayName}${finMgr}`
  }
  if (
    (role_name === 'ROLE_PROPERTY_ADMIN' ||
      role_name === 'ROLE_PROPERTY_MANAGER' ||
      role_name === 'ROLE_PROPERTY_AGENT') &&
    property_name
  ) {
    return `${property_name} - ${displayName}${finMgr}`
  }
  if (
    (role_name === 'ROLE_ATLANTA_OPEN_DOOR_ADMIN' ||
      role_name === 'ROLE_ATLANTA_OPEN_DOOR_MANAGER') &&
    org_name
  ) {
    return `${org_name} - ${displayName}${finMgr}`
  }
  return `${displayName}${finMgr}`
}

/**
 * Split role display into bold prefix and regular suffix for the Roles table.
 * Role column displays ONLY the clean role name (+ optional Finance Manager suffix).
 * Entity / Company name is displayed in the Company column.
 */
export function buildRoleDisplayParts(role: UserRole): { boldPrefix: string; suffix: string } {
  const { role_name } = role
  const finMgr = role.fin_mgr_attribute === 'YES' ? ' (Finance Manager)' : ''

  const displayName = ROLE_DISPLAY_MAP[role_name] || formatRoleKey(role_name)

  return { boldPrefix: '', suffix: `${displayName}${finMgr}` }
}

/**
 * Format entity/company name for the Company column in the Roles table.
 * ONLY returns a value for scoped role types that associate an entity with the role
 * (Company Manager/Admin, Property Manager/Agent/Admin, Regional Manager, Charity/Open Door roles).
 * Returns empty string ('—') for non-scoped roles (Resident, Liberty internal roles, etc.).
 */
export function formatRoleCompanyDisplay(role: UserRole): string {
  const { role_name, company_name, property_name, org_name } = role

  if (role_name === 'ROLE_REGIONAL_MANAGER' && company_name) {
    return property_name ? `${company_name} (${property_name})` : company_name
  }

  if (
    (role_name === 'ROLE_COMPANY_MANAGER' || role_name === 'ROLE_COMPANY_ADMIN') &&
    company_name
  ) {
    return company_name
  }

  if (
    role_name === 'ROLE_PROPERTY_ADMIN' ||
    role_name === 'ROLE_PROPERTY_MANAGER' ||
    role_name === 'ROLE_PROPERTY_AGENT'
  ) {
    return property_name || company_name || ''
  }

  if (
    role_name === 'ROLE_ATLANTA_OPEN_DOOR_ADMIN' ||
    role_name === 'ROLE_ATLANTA_OPEN_DOOR_MANAGER'
  ) {
    return org_name || company_name || ''
  }

  return ''
}

/**
 * Format a datetime string with MM/DD/YY date format while retaining the timestamp (MM/dd/yy hh:mm:ss AM/PM).
 */
export function formatDateTime(value: string | number | null | undefined): string {
  if (!value && value !== 0) return ''
  try {
    const str = String(value).trim()
    if (!str || str === '—' || str === 'null' || str === 'undefined') return ''

    // 1. Match YYYY-MM-DD HH:mm:ss or YYYY-MM-DDTHH:mm:ss
    const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/i)
    if (ymdMatch) {
      const year = ymdMatch[1].slice(-2)
      const month = ymdMatch[2].padStart(2, '0')
      const day = ymdMatch[3].padStart(2, '0')

      if (ymdMatch[4]) {
        let hours = parseInt(ymdMatch[4], 10)
        const minutes = ymdMatch[5] || '00'
        const seconds = ymdMatch[6] || '00'

        // Check for trailing AM/PM in original string
        const ampmMatch = str.match(/(AM|PM)/i)
        let ampm = ampmMatch ? ampmMatch[1].toUpperCase() : ''
        if (!ampm) {
          ampm = hours >= 12 ? 'PM' : 'AM'
          hours = hours % 12 || 12
        }
        const formattedHours = String(hours).padStart(2, '0')
        return `${month}/${day}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`
      }
      return `${month}/${day}/${year}`
    }

    // 2. Match MM-DD-YYYY or MM/DD/YYYY (e.g. "05-06-2025 07:43:15 AM")
    const mdyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})(?:\s+(.*))?/)
    if (mdyMatch) {
      const month = mdyMatch[1].padStart(2, '0')
      const day = mdyMatch[2].padStart(2, '0')
      let year = mdyMatch[3]
      if (year.length === 4) year = year.slice(-2)
      const timePart = mdyMatch[4] ? ` ${mdyMatch[4].trim()}` : ''
      return `${month}/${day}/${year}${timePart}`
    }

    // 3. Fallback to Date object parsing
    const num = typeof value === 'number' ? value : Number(str)
    let ts: number | string = str
    if (!isNaN(num) && num > 0) {
      ts = num < 10_000_000_000 ? num * 1000 : num
    } else {
      ts = str.replace(' ', 'T')
    }
    const d = new Date(ts)
    if (isNaN(d.getTime())) return str
    const pad = (n: number) => String(n).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    let hours = d.getHours()
    const minutes = pad(d.getMinutes())
    const seconds = pad(d.getSeconds())
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    return `${month}/${day}/${year} ${pad(hours)}:${minutes}:${seconds} ${ampm}`
  } catch {
    return value === undefined || value === null ? '' : String(value)
  }
}

/**
 * Format a date only string for display (MM/dd/yy).
 * Handles YYYY-MM-DD HH:mm:ss, MM-DD-YYYY, ISO strings, and unix timestamps.
 */
export function formatDateOnly(value: string | number | null | undefined): string {
  if (!value && value !== 0) return ''
  try {
    const str = String(value).trim()
    if (!str || str === '—' || str === 'null' || str === 'undefined') return ''

    // 1. Match MM-DD-YYYY or MM/DD/YYYY with optional time
    const mdyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/)
    if (mdyMatch) {
      const month = mdyMatch[1].padStart(2, '0')
      const day = mdyMatch[2].padStart(2, '0')
      let year = mdyMatch[3]
      if (year.length === 4) year = year.slice(-2)
      return `${month}/${day}/${year}`
    }

    // 2. Match YYYY-MM-DD
    const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
    if (ymdMatch) {
      const year = ymdMatch[1].slice(-2)
      const month = ymdMatch[2].padStart(2, '0')
      const day = ymdMatch[3].padStart(2, '0')
      return `${month}/${day}/${year}`
    }

    const num = typeof value === 'number' ? value : Number(str)
    let ts: number | string = str
    if (!isNaN(num) && num > 0) {
      ts = num < 10_000_000_000 ? num * 1000 : num
    }
    const d = new Date(ts)
    if (isNaN(d.getTime())) return str
    const pad = (n: number) => String(n).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    return `${month}/${day}/${year}`
  } catch {
    return value === undefined || value === null ? '' : String(value)
  }
}

/**
 * Build a safe HTML error message matching Angular's structured error format.
 * String → single <li>; object → one <li> per value.
 */
export function buildApiErrorHtml(error: unknown): string {
  if (typeof error === 'string') {
    return `<ul class='error-messages-list'><li>${error}</li></ul>`
  }
  if (error && typeof error === 'object' && !Array.isArray(error)) {
    const values = Object.values(error as Record<string, unknown>)
    if (values.length > 0) {
      const items = values.map((v) => `<li>${String(v)}</li>`).join('')
      return `<ul class='error-messages-list'>${items}</ul>`
    }
  }
  return `<ul class='error-messages-list'><li>An error occurred.</li></ul>`
}

/**
 * Angular user list parity: `liberty_roleslist.includes(user?.all_roles)` on the list API field.
 * `rolename[].rolename` is a display label (e.g. "Property Manager"), not `ROLE_*`, so do not use it here.
 */
export function isUserListRowLibertyAllRoles(user: { all_roles?: string | null }): boolean {
  const raw = user.all_roles
  if (raw == null) return false
  const s = String(raw).trim()
  if (!s) return false
  return (LIBERTY_ROLE_KEYS as readonly string[]).includes(s as (typeof LIBERTY_ROLE_KEYS)[number])
}

export interface CategorizedRoleGroup {
  category: string
  roles: string[]
}

/**
 * Categorizes a list of role display names into exact headings and order:
 * "LR Roles", "Company Roles", "Property Roles", "Resident", "Charity Roles", "Other Roles".
 */
export function categorizeUserRoles(roleNames: string[]): CategorizedRoleGroup[] {
  const lr: string[] = []
  const company: string[] = []
  const property: string[] = []
  const charity: string[] = []
  const resident: string[] = []
  const other: string[] = []

  roleNames.forEach((name) => {
    const trimmed = name.trim()
    if (!trimmed) return
    const lower = trimmed.toLowerCase()

    // 1. Pure LR Roles (Liberty Rent internal roles: "Liberty Admin", "Liberty Customer Service", etc.)
    const isPureLiberty =
      (lower.startsWith('liberty') || lower.includes('liberty ')) &&
      !lower.includes('- company') &&
      !lower.includes('- property') &&
      !lower.includes('company admin') &&
      !lower.includes('company manager') &&
      !lower.includes('company regional') &&
      !lower.includes('property manager') &&
      !lower.includes('property assistant') &&
      !lower.includes('leasing consultant')

    if (isPureLiberty) {
      lr.push(trimmed)
      return
    }

    // 2. Company Roles
    if (
      lower.includes('- company') ||
      lower.includes('company admin') ||
      lower.includes('company manager') ||
      lower.includes('company regional manager') ||
      /\bcompany\b/.test(lower)
    ) {
      company.push(trimmed)
      return
    }

    // 3. Property Roles
    if (
      lower.includes('- property') ||
      lower.includes('property manager') ||
      lower.includes('property assistant') ||
      lower.includes('leasing consultant') ||
      /\bproperty\b/.test(lower)
    ) {
      property.push(trimmed)
      return
    }

    // 4. Charity Roles
    if (lower.includes('charity') || lower.includes('atlanta') || lower.includes('open door')) {
      charity.push(trimmed)
      return
    }

    // 5. Resident / Renter / Applicant Roles (use \b so "residential" will NOT match "resident")
    if (
      /\brenter\b/.test(lower) ||
      /\bresident\b/.test(lower) ||
      /\bapplicant\b/.test(lower)
    ) {
      resident.push(trimmed)
      return
    }

    // Fallback if role starts with Liberty or mentions company/property
    if (lower.startsWith('liberty')) {
      lr.push(trimmed)
      return
    }

    other.push(trimmed)
  })

  const result: CategorizedRoleGroup[] = []
  if (lr.length > 0) result.push({ category: 'LR Roles', roles: lr })
  if (company.length > 0) result.push({ category: 'Company Roles', roles: company })
  if (property.length > 0) result.push({ category: 'Property Roles', roles: property })
  if (charity.length > 0) result.push({ category: 'Charity Roles', roles: charity })
  if (resident.length > 0) result.push({ category: 'Resident', roles: resident })
  if (other.length > 0) result.push({ category: 'Other Roles', roles: other })

  return result
}
